import { AxiosError } from 'axios';
import {
  ApiError,
  NetworkError,
  TimeoutError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  ValidationError,
  InternalServerError,
  BadGatewayError,
  ServiceUnavailableError,
  ClientError,
  ServerError,
} from './ApiError';

/**
 * Error Handler
 * Converts Axios errors and other errors into domain error types
 */

/**
 * Convert an Axios error to a domain ApiError
 */
export function handleApiError(error: unknown): ApiError {
  // If it's already an ApiError, return it
  if (error instanceof ApiError) {
    return error;
  }

  // Handle Axios errors
  if (isAxiosError(error)) {
    return convertAxiosError(error);
  }

  // Handle generic errors
  if (error instanceof Error) {
    return new ApiError(error.message, undefined, 'UNKNOWN_ERROR', error);
  }

  // Handle unknown error types
  return new ApiError('An unknown error occurred', undefined, 'UNKNOWN_ERROR', error);
}

/**
 * Type guard for AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as { isAxiosError?: boolean }).isAxiosError === true
  );
}

/**
 * Convert AxiosError to appropriate domain error type
 */
function convertAxiosError(error: AxiosError): ApiError {
  // Network error - no response received
  if (error.request && !error.response) {
    // Check if it's a timeout
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new TimeoutError('Request timeout', error);
    }
    return new NetworkError('Network error - no response received', error);
  }

  // Server responded with error status
  if (error.response) {
    const status = error.response.status;
    const responseData = error.response.data as { message?: string; errors?: unknown; validationErrors?: unknown } | undefined;
    const message = responseData?.message || error.message || 'API request failed';
    const details = responseData;

    // 4xx Client Errors
    if (status >= 400 && status < 500) {
      switch (status) {
        case 401:
          return new UnauthorizedError(message, details, error);
        case 403:
          return new ForbiddenError(message, details, error);
        case 404:
          return new NotFoundError(message, details, error);
        case 422:
          return new ValidationError(
            message,
            (responseData?.errors || responseData?.validationErrors) as Record<string, string[]> | undefined,
            error
          );
        default:
          return new ClientError(message, status, details, error);
      }
    }

    // 5xx Server Errors
    if (status >= 500) {
      switch (status) {
        case 500:
          return new InternalServerError(message, details, error);
        case 502:
          return new BadGatewayError(message, details, error);
        case 503:
          return new ServiceUnavailableError(message, details, error);
        default:
          return new ServerError(message, status, details, error);
      }
    }
  }

  // Fallback to generic ApiError
  return new ApiError(
    error.message || 'An error occurred',
    error.response?.status,
    error.code,
    error
  );
}

/**
 * Check if error is a specific error type
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError;
}

export function isClientError(error: unknown): error is ClientError {
  return error instanceof ClientError;
}

export function isServerError(error: unknown): error is ServerError {
  return error instanceof ServerError;
}

export function isUnauthorizedError(error: unknown): error is UnauthorizedError {
  return error instanceof UnauthorizedError;
}

export function isForbiddenError(error: unknown): error is ForbiddenError {
  return error instanceof ForbiddenError;
}

export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

